/**
 * ToolUseIndicator Component
 *
 * Displays a contextual status pill when the AI assistant is working —
 * thinking, searching past conversations, or reading a transcript.
 * Designed to always show some visible state during streaming so the
 * user knows something is happening.
 */

import { Search, FileText, Sparkles, Loader2 } from "lucide-react";

interface ToolUseIndicatorProps {
  toolName: string | null;
  /**
   * Whether the AI is actively streaming/processing. When true with no
   * toolName, shows a generic "Thinking..." state.
   */
  isStreaming?: boolean;
}

const TOOL_DISPLAY: Record<string, { icon: typeof Search; label: string }> = {
  list_reflections: {
    icon: Search,
    label: "Searching past conversations",
  },
  get_transcript: {
    icon: FileText,
    label: "Reading transcript",
  },
};

export function ToolUseIndicator({
  toolName,
  isStreaming = false,
}: ToolUseIndicatorProps) {
  // Show tool name if active, otherwise "Thinking..." while streaming
  let Icon: typeof Search;
  let label: string;

  if (toolName) {
    const display = TOOL_DISPLAY[toolName] ?? {
      icon: Loader2,
      label: `Using ${toolName}`,
    };
    Icon = display.icon;
    label = display.label;
  } else if (isStreaming) {
    Icon = Sparkles;
    label = "Thinking";
  } else {
    return null;
  }

  return (
    <div className="flex justify-start">
      <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-muted text-sm text-muted-foreground">
        <Icon className="h-3.5 w-3.5 animate-pulse" />
        <span className="font-medium">{label}</span>
        <span className="flex gap-0.5">
          <span className="w-1 h-1 rounded-full bg-muted-foreground/60 animate-bounce [animation-delay:0ms]" />
          <span className="w-1 h-1 rounded-full bg-muted-foreground/60 animate-bounce [animation-delay:150ms]" />
          <span className="w-1 h-1 rounded-full bg-muted-foreground/60 animate-bounce [animation-delay:300ms]" />
        </span>
      </div>
    </div>
  );
}
